import type { BusinessScale } from '@billx/types';
import { BUSINESS_TYPES, getLabel } from '@billx/utils';
import {
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { FieldLabel } from '@/components/common';

import { FloatingHelperTextField } from '../FloatingHelperTextField';
import type { BusinessInfoData } from './schemas';

interface BusinessInfoFormProps {
  disabled?: boolean;
}

type BusinessScaleOption = {
  value: BusinessScale;
  label: string;
};

const businessScales: BusinessScaleOption[] = [
  { value: 'single_outlet', label: 'Single Outlet' },
  { value: 'multi_outlet', label: 'Multi Outlet' },
  { value: 'enterprise', label: 'Enterprise Chain' },
];

export const BusinessInfoForm: React.FC<BusinessInfoFormProps> = ({
  disabled = false,
}) => {
  const { control } = useFormContext<{ business: BusinessInfoData }>();
  const gap = { xs: 2, sm: 3 };
  return (
    <Stack gap={gap}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap,
        }}
      >
        <Controller
          name="business.name"
          control={control}
          render={({ field, fieldState }) => (
            <Box>
              <FieldLabel
                htmlFor="business.name"
                label="Business Name"
                required
                subtitle="Your business trade name / DBA"
              />
              <FloatingHelperTextField
                {...field}
                addMarginBottom={false}
                placeholder="Business Name"
                fullWidth
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
                disabled={disabled}
                required
              />
            </Box>
          )}
        />
        <Controller
          name="business.legalName"
          control={control}
          render={({ field, fieldState }) => (
            <Box>
              <FieldLabel
                htmlFor="business.legalName"
                label="Legal Name"
                required
                subtitle="Legal business name as per the GST certificate"
              />
              <FloatingHelperTextField
                {...field}
                addMarginBottom={false}
                placeholder="Legal Business Name"
                fullWidth
                error={fieldState.invalid}
                helperText={fieldState.error?.message}
              />
            </Box>
          )}
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
          gap,
        }}
      >
        <Controller
          name="business.type"
          control={control}
          render={({ field, fieldState }) => (
            <Box>
              <FieldLabel
                htmlFor="business.type"
                label="Business Type"
                required
              />
              <FormControl
                fullWidth
                error={fieldState.invalid}
                disabled={disabled}
              >
                <Select {...field} fullWidth required displayEmpty>
                  <MenuItem value="">
                    <Typography variant="subtitle2" fontStyle="italic">
                      Select business type
                    </Typography>
                  </MenuItem>
                  {BUSINESS_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {getLabel('BusinessType', type)}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText
                  error={fieldState.invalid}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    transform: 'translateY(120%)',
                  }}
                >
                  {fieldState.error?.message}
                </FormHelperText>
              </FormControl>
            </Box>
          )}
        />

        <Controller
          name="business.scale"
          control={control}
          render={({ field, fieldState }) => (
            <Box>
              <FieldLabel
                htmlFor="business.scale"
                label="Business Scale"
                required
              />
              <FormControl
                fullWidth
                error={fieldState.invalid}
                disabled={disabled}
              >
                <Select {...field} fullWidth required displayEmpty>
                  <MenuItem value="">
                    <Typography variant="subtitle2" fontStyle="italic">
                      Select business scale
                    </Typography>
                  </MenuItem>
                  {businessScales.map((scale) => (
                    <MenuItem key={scale.value} value={scale.value}>
                      {scale.label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText
                  error={fieldState.invalid}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    transform: 'translateY(120%)',
                  }}
                >
                  {fieldState.error?.message}
                </FormHelperText>
              </FormControl>
            </Box>
          )}
        />
      </Box>
    </Stack>
  );
};
